import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiResponse, apiValue } from '@classes/api-response';

export interface CommunicationTemplate {
  _id?: string;
  name?: string;
  category?: string;
  channel?: string;
  messageType?: string;
  subject?: string;
  previewText?: string;
  body?: string;
  fromName?: string;
  fromAddress?: string;
  replyTo?: string;
  logoUrl?: string;
  headerColor?: string;
  footer?: string;
  status?: string;
  version?: number;
  testedAt?: string | Date;
  lastEditorName?: string;
  dateUpdated?: string | Date;
  versions?: Array<{
    version?: number;
    name?: string;
    subject?: string;
    body?: string;
    previewText?: string;
    savedAt?: string | Date;
    editorName?: string;
  }>;
}

export interface CommunicationRule {
  _id?: string;
  name?: string;
  trigger?: string;
  category?: string;
  templateId?: string;
  timingDays?: number;
  recurring?: boolean;
  status?: string;
  testedAt?: string | Date;
  lastActivatedAt?: string | Date;
  lastEditorName?: string;
  dateUpdated?: string | Date;
}

@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private baseUrl = `${environment.base_url}/communication`;

  constructor(private http: HttpClient) {}

  status(): Observable<any> {
    return this.http.get(`${this.baseUrl}/status`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'status', {})),
      catchError(() => of({ alerts: ['Could not load provider status'] }))
    );
  }

  listTemplates(): Observable<CommunicationTemplate[]> {
    return this.http.get(`${this.baseUrl}/templates`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'templates', [])),
      catchError(() => of([]))
    );
  }

  saveTemplate(template: CommunicationTemplate): Observable<CommunicationTemplate> {
    return this.http
      .put(`${this.baseUrl}/templates/${template._id}`, template)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'template')));
  }

  testTemplate(id: string, to: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/templates/${id}/test`, { to });
  }

  restoreTemplate(id: string, version: number): Observable<CommunicationTemplate> {
    return this.http
      .post(`${this.baseUrl}/templates/${id}/restore`, { version })
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'template')));
  }

  previewTemplate(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/templates/${id}/preview`);
  }

  listRules(): Observable<CommunicationRule[]> {
    return this.http.get(`${this.baseUrl}/rules`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'rules', [])),
      catchError(() => of([]))
    );
  }

  saveRule(rule: CommunicationRule): Observable<CommunicationRule> {
    return this.http
      .put(`${this.baseUrl}/rules/${rule._id}`, rule)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'rule')));
  }

  activateRule(id: string): Observable<CommunicationRule> {
    return this.http
      .post(`${this.baseUrl}/rules/${id}/activate`, {})
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'rule')));
  }

  pauseRule(id: string): Observable<CommunicationRule> {
    return this.http
      .post(`${this.baseUrl}/rules/${id}/pause`, {})
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'rule')));
  }

  estimateRule(id: string): Observable<number> {
    return this.http.get(`${this.baseUrl}/rules/${id}/estimate`).pipe(
      map((res: ApiResponse<any>) => Number(apiValue(res, 'count', 0)) || 0),
      catchError(() => of(0))
    );
  }

  listSends(status?: string): Observable<any[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get(`${this.baseUrl}/sends${query}`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'sends', [])),
      catchError(() => of([]))
    );
  }

  listExceptions(): Observable<any[]> {
    return this.http.get(`${this.baseUrl}/exceptions`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'exceptions', [])),
      catchError(() => of([]))
    );
  }

  listAudit(): Observable<any[]> {
    return this.http.get(`${this.baseUrl}/audit`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'audit', [])),
      catchError(() => of([]))
    );
  }

  runNow(): Observable<any> {
    return this.http.post(`${this.baseUrl}/run`, {});
  }

  getRxExpirationTemplate(): Observable<CommunicationTemplate> {
    return this.http.get(`${this.baseUrl}/template/rx-expiration`).pipe(
      map((res: ApiResponse<any>) =>
        apiValue(res, 'template', { name: 'RX Expiration', subject: '', body: '' })
      ),
      catchError(() => of({ name: 'RX Expiration', subject: '', body: '' }))
    );
  }

  saveRxExpirationTemplate(
    template: CommunicationTemplate
  ): Observable<CommunicationTemplate> {
    return this.http
      .put(`${this.baseUrl}/template/rx-expiration`, template)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'template')));
  }

  listLog(): Observable<any[]> {
    return this.listSends();
  }
}
